
// frontend/src/components/Sports/StandingsTable.js
// Premium Standings Table with SEO-Friendly Team Links
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useStandings } from '../../services/SportsDataService';
import { Trophy, TrendingUp, TrendingDown, Minus } from 'lucide-react';

// Convert team name to SEO-friendly slug
const teamNameToSlug = (name) => {
    if (!name) return '';
    return name
        .toLowerCase()
        .replace(/['']/g, '')  // Remove apostrophes
        .replace(/\s+/g, '-')  // Spaces to hyphens
        .replace(/[^a-z0-9-]/g, '')  // Remove special chars
        .replace(/-+/g, '-')  // Multiple hyphens to single
        .replace(/(^-|-$)/g, '');  // Trim hyphens
};

const FormIndicator = ({ result }) => {
    const colors = {
        'W': 'bg-green-500',
        'L': 'bg-red-500',
        'D': 'bg-yellow-500',
        'T': 'bg-yellow-500'
    };
    return (
        <span className={`w-5 h-5 flex items-center justify-center text-xs font-bold rounded ${colors[result] || 'bg-gray-500'} text-white`}>
            {result}
        </span>
    );
};

export const StandingsTable = ({ sport, league, leagueSlug, limit = 10 }) => {
    const { standings, isLoading, isError } = useStandings(sport, league);

    if (isLoading) {
        return (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-8">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-1 h-8 bg-gradient-to-b from-primary to-blue-500 rounded-full" />
                    <div className="h-6 w-40 bg-white/10 rounded animate-pulse" />
                </div>
                <div className="space-y-3">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="h-12 bg-white/5 rounded-lg animate-pulse" />
                    ))}
                </div>
            </div>
        );
    }

    if (isError || standings.length === 0) {
        return (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center">
                <p className="text-muted-foreground">Standings not available</p>
            </div>
        );
    }

    // Take top N teams
    const displayStandings = standings.slice(0, limit);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-white/10 overflow-hidden"
            style={{
                background: 'rgba(255, 255, 255, 0.02)',
                backdropFilter: 'blur(12px)',
            }}
        >
            {/* Header */}
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3">
                <Trophy className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-bold text-white">League Standings</h3>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-white/5 text-xs text-muted-foreground uppercase tracking-wider">
                            <th className="py-3 px-4 text-left">#</th>
                            <th className="py-3 px-4 text-left">Team</th>
                            <th className="py-3 px-2 text-center">P</th>
                            <th className="py-3 px-2 text-center">W</th>
                            <th className="py-3 px-2 text-center">D</th>
                            <th className="py-3 px-2 text-center">L</th>
                            <th className="py-3 px-2 text-center hidden sm:table-cell">GD</th>
                            <th className="py-3 px-4 text-center font-bold text-primary">PTS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayStandings.map((entry, index) => {
                            const team = entry.team;
                            const stats = entry.stats || [];

                            // Extract stats by name
                            const getStat = (name) => {
                                const stat = stats.find(s => s.name === name || s.abbreviation === name);
                                return stat?.value || stat?.displayValue || '0';
                            };

                            const position = index + 1;
                            const isTop = position <= 4;
                            const isBottom = position > limit - 3;

                            return (
                                <motion.tr
                                    key={team?.id || index}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: index * 0.03 }}
                                    className={`
                                        border-b border-white/5 last:border-0
                                        hover:bg-white/5 transition-colors
                                        ${isTop ? 'bg-green-500/5' : ''}
                                        ${isBottom ? 'bg-red-500/5' : ''}
                                    `}
                                >
                                    <td className="py-3 px-4">
                                        <span className={`
                                            font-bold text-sm
                                            ${isTop ? 'text-green-400' : ''}
                                            ${isBottom ? 'text-red-400' : 'text-muted-foreground'}
                                        `}>
                                            {position}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4">
                                        <Link
                                            to={`/sports/${sport}/${leagueSlug}/team/${teamNameToSlug(team?.shortDisplayName || team?.displayName)}`}
                                            className="flex items-center gap-3 group"
                                        >
                                            {team?.logos?.[0]?.href && (
                                                <img
                                                    src={team.logos[0].href}
                                                    alt={team.displayName}
                                                    className="w-6 h-6 object-contain group-hover:scale-110 transition-transform"
                                                />
                                            )}
                                            <span className="font-medium text-white text-sm truncate max-w-[120px] sm:max-w-none group-hover:text-primary transition-colors">
                                                {team?.shortDisplayName || team?.displayName || 'Unknown'}
                                            </span>
                                        </Link>
                                    </td>
                                    <td className="py-3 px-2 text-center text-sm text-muted-foreground">
                                        {getStat('gamesPlayed') || getStat('GP')}
                                    </td>
                                    <td className="py-3 px-2 text-center text-sm text-green-400">
                                        {getStat('wins') || getStat('W')}
                                    </td>
                                    <td className="py-3 px-2 text-center text-sm text-yellow-400">
                                        {getStat('ties') || getStat('draws') || getStat('D') || getStat('T')}
                                    </td>
                                    <td className="py-3 px-2 text-center text-sm text-red-400">
                                        {getStat('losses') || getStat('L')}
                                    </td>
                                    <td className="py-3 px-2 text-center text-sm text-muted-foreground hidden sm:table-cell">
                                        {getStat('pointDifferential') || getStat('GD') || getStat('differential') || '-'}
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <span className="font-bold text-white text-lg">
                                            {getStat('points') || getStat('PTS') || getStat('wins')}
                                        </span>
                                    </td>
                                </motion.tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </motion.div>
    );
};
